import visibilityIcon from "@ms/visibility.svg?raw";
import { BrandIcon } from "@ui/BrandIcon";
import { Button } from "@ui/Button";
import { CoverButton } from "@ui/CoverButton";
import { Icon } from "@ui/Icon";
import { MaterialSymbol } from "@ui/MaterialSymbol";
import { Pill } from "@ui/Pill";
import { Stack } from "@ui/Stack";
import { formatNullableCount } from "@utils/formatNullableCount";
import { secondsToTime } from "@utils/time";
import {
	type Accessor,
	createEffect,
	createMemo,
	createSignal,
	type JSX,
	onCleanup,
	onMount,
	type ParentComponent,
	Show,
	untrack,
} from "solid-js";
import "./VideoPreview.css";
import { usePendingImage } from "@ui/hooks/usePendingImage";
import { Slider } from "@ui/Slider";
import { Video } from "@ui/Video";
import {
	attachHlsClip,
	type HlsClipSource,
	type HlsSourceHandle,
} from "./useHlsSource";
import { useVolumeControl, VolumeControl } from "./VolumeControl";
import {
	ensurePointerActivityTracking,
	msSincePointerSettled,
} from "./videoPreviewPointerActivity";
import {
	createVideoPreviewOriginatorId,
	VIDEO_PREVIEW_PAUSE_ALL_EVENT,
} from "./videoPreviewVolume";
import { createWarmVideoSlot } from "./videoPreviewWarmRegistry";

type Props = {
	/**
	 * Makes the whole preview activate, via a transparent button covering it
	 * rather than a button wrapping it — the controls over a preview are
	 * buttons themselves, and may not be nested inside another one.
	 */
	onActivate?: (event: MouseEvent) => void;
	/** Names the activating button, which has no content to name it. */
	activateLabel?: string;
	thumbnailUrl?: string;
	/** Optional start time of the video clip. This is where the video should play from. */
	startSeconds?: number;
	/** Absolute end time in the source (seconds). Defaults to the media duration once metadata loads. */
	endSeconds?: number;
	title?: string;
	platform?: string;
	/** Undefined omits the count; null shows that it is unavailable. */
	views?: number | null;
	videoUrl: string;
	/**
	 * When set, tries to play the video's HLS package before falling back to
	 * `videoUrl` (e.g. a video with no package yet). Must span the whole
	 * source (see fullVideoHlsSource): a manifest starting anywhere else has
	 * its own timeline, which `startSeconds`/`endSeconds` -- in `videoUrl`'s
	 * timeline -- would no longer line up with. So only pass it when
	 * `videoUrl` is the whole source video too, never a pre-cut clip.
	 */
	hlsSource?: HlsClipSource;
	handle?: string;
	footer?: JSX.Element;
	playTrigger?: "hover" | "none";
	showDuration?: boolean;
	/**
	 * Makes the duration pill a button. `symbol` follows the timecode to mark
	 * what the pill does; `done` holds it still once there is nothing left to
	 * do. Ignored when `showDuration` is false, since the pill is what carries it.
	 */
	durationAction?: {
		symbol: string;
		label: string;
		onActivate: () => void;
		done?: boolean;
	};
	/** When true and the preview is eligible to play (`disabled`/`forcePause` false), tries to start playback (muted — browser autoplay policies). Default: false. */
	autoPlay?: boolean;
	/** When true, looping applies to the clip between `startSeconds` and `endSeconds` only (not the full source file). Default: false. */
	loop?: boolean;
	/** Bottom overlay: play, seek, times, mute (clip-relative when start/end are set). */
	showControls?: boolean;
	/**
	 * When to add `footer--hidden` (ignored if `showControls` is true).
	 * `play` — hide footer while hovered or hover-play is active (default).
	 * `pause` — hide footer when idle (show footer on hover / play intent).
	 * `never` — always show footer when it has content.
	 * `always` — suppress the footer gradient only; visibility follows `pause`.
	 */
	footerHiddenOn?: "play" | "pause" | "never" | "always";
	/** When true, forces the video element muted (e.g. carousel scrolled away from this slide). */
	forceMuteAudio?: boolean;
	/** When true, forces the video element paused (e.g. carousel scrolled away from this slide). */
	forcePause?: boolean;
	/** When true, forces the children to be show. Otherwise only shown when hovered or playing. */
	forceShowChildren?: boolean;
	/** When true, disables the video element (e.g. carousel scrolled away from this slide). */
	disabled?: boolean;
	/** 9:16 portrait frame; drops default min-height so aspect-ratio controls size. */
	portrait?: boolean;
	class?: string;
	style?: JSX.CSSProperties;
	/**
	 * Called with clip-relative seconds whenever the displayed playback position updates
	 * (timeupdate, seek, loaded metadata).
	 */
	onPlaybackTime?: (clipRelativeSeconds: number) => void;
	/** Fired when the underlying `<video>` begins playback. */
	onPlay?: () => void;
	/** Fired when the underlying `<video>` pauses. */
	onPause?: () => void;
	/** Native pixel size after `loadedmetadata` (e.g. choose portrait vs landscape layout). */
	onVideoDimensions?: (size: { width: number; height: number }) => void;
	/** Native duration after `loadedmetadata`, in seconds. */
	onVideoDuration?: (durationSeconds: number) => void;
	/**
	 * Increment `id` to seek within the current trim to `clipRelativeSeconds` (0 … end − start)
	 * without changing `startSeconds` / `endSeconds`.
	 */
	playbackSeekRequest?: Accessor<
		{ id: number; clipRelativeSeconds: number } | undefined
	>;
};

/** Absolute start/end in the source file (seconds). */
export type VideoPreviewClipBounds = () => {
	startSeconds: number;
	endSeconds: number;
};

/**
 * How long the pointer must be *settled* (no move, no scroll) over a card before
 * a cold preview loads and plays. Because it's gated on settling rather than
 * mere presence, a pointer travelling across the grid never triggers it.
 */
const HOVER_ACTIVATION_DELAY_MS = 150;

/**
 * Settle window used while "browsing mode" is active (the user has recently
 * played a preview) or for an already-warm clip. Shorter so deliberate
 * clip-to-clip movement feels immediate, but still requires the pointer to
 * settle — so passing over a card, or a card scrolling under a stationary
 * pointer, doesn't reveal/play it (which would flicker the placeholder).
 */
const HOVER_ACTIVATION_DELAY_BROWSING_MS = 60;

/** Minimum gap between settle re-checks while waiting for the pointer to rest. */
const POINTER_SETTLE_POLL_MIN_MS = 16;

/**
 * How long browsing mode stays active after the last interaction. A pause
 * longer than this resets to the cautious full dwell on the next hover.
 */
const BROWSING_MODE_IDLE_MS = 600;

/**
 * Shared across all previews: timestamp (ms, monotonic) until which browsing
 * mode is considered active. Armed only when a clip actually plays, so pure
 * sweeps (where nothing plays) never shorten the dwell.
 */
let browsingModeActiveUntil = Number.NEGATIVE_INFINITY;
const isBrowsingModeActive = () => performance.now() < browsingModeActiveUntil;
const refreshBrowsingMode = () => {
	browsingModeActiveUntil = performance.now() + BROWSING_MODE_IDLE_MS;
};

export const useVideoPreview = (
	getVideoRef: () => HTMLVideoElement | undefined,
	hasThumbnail: boolean,
	getClipBounds?: VideoPreviewClipBounds,
	sourceControls?: {
		/** Mark the source as needed so the `<video>` can begin loading. */
		activateSource?: () => void;
		/** Drop the source so open connections to the media server are released. */
		deactivateSource?: () => void;
		/**
		 * When true, the clip is already loaded/buffered from a previous play, so
		 * hover can start it immediately (no dwell) and it stays warm on leave.
		 */
		isWarm?: () => boolean;
	},
) => {
	const [playing, setPlaying] = createSignal(false);
	const [videoStyle, setVideoStyle] = createSignal<JSX.CSSProperties>({
		opacity: hasThumbnail ? 0 : 1,
		display: hasThumbnail ? "none" : "block",
	});

	const seekToClipStartIfNeeded = (el: HTMLVideoElement) => {
		if (!getClipBounds) {
			return;
		}
		const { startSeconds, endSeconds } = getClipBounds();
		if (el.currentTime < startSeconds || el.currentTime >= endSeconds) {
			el.currentTime = startSeconds;
		}
	};

	let timeout: number;
	const playWithRetry = (el: HTMLVideoElement) => {
		seekToClipStartIfNeeded(el);
		const tryPlay = () => {
			el.play().catch(() => {
				// Retry once media is ready enough to play.
				el.addEventListener(
					"canplay",
					() => {
						seekToClipStartIfNeeded(el);
						el.play().catch(() => {});
					},
					{ once: true },
				);
			});
		};

		tryPlay();
	};

	const playVideoOnHover = (play: boolean) => {
		clearTimeout(timeout);
		ensurePointerActivityTracking();
		const start = () => {
			sourceControls?.activateSource?.();
			if (hasThumbnail) {
				setVideoStyle({
					opacity: 1,
					display: "block",
				});
			}
			const el = getVideoRef();
			if (!el) return;
			setPlaying(play);
			playWithRetry(el);
		};
		// Wait for the pointer to actually settle over the card before playing —
		// gating on stillness (no move/scroll) rather than elapsed presence means a
		// pointer travelling across the grid, or a card scrolling under a
		// stationary pointer, never triggers playback (and so never flickers). A
		// warm clip or an active browsing session uses the shorter settle window so
		// deliberate hovers still feel immediate.
		const waitForSettle = () => {
			const nearInstant =
				isBrowsingModeActive() || (sourceControls?.isWarm?.() ?? false);
			const requiredQuiet = nearInstant
				? HOVER_ACTIVATION_DELAY_BROWSING_MS
				: HOVER_ACTIVATION_DELAY_MS;
			const quietFor = msSincePointerSettled();
			if (quietFor >= requiredQuiet) {
				refreshBrowsingMode();
				start();
				return;
			}
			timeout = setTimeout(
				waitForSettle,
				Math.max(POINTER_SETTLE_POLL_MIN_MS, requiredQuiet - quietFor),
			);
		};
		waitForSettle();
	};

	const handleMouseLeave = () => {
		if (hasThumbnail) {
			setVideoStyle({
				opacity: 0,
				display: "none",
			});
		}
		clearTimeout(timeout);
		setPlaying(false);
		const el = getVideoRef();
		if (el) {
			el.pause();
			if (getClipBounds) {
				el.currentTime = getClipBounds().startSeconds;
			}
		}
		// Release the source once the pointer leaves so lazily-loaded previews
		// don't keep the media server's connections/workers occupied. Clips that
		// have already played stay warm (kept loaded) for instant replay.
		if (!sourceControls?.isWarm?.()) {
			sourceControls?.deactivateSource?.();
		}
	};

	return {
		playing,
		playVideoOnHover,
		handleMouseLeave,
		videoStyle,
	};
};

/** When the trim end moves, preview seeks this many seconds before the new end (clamped to trim start). */
const PREVIEW_LEAD_BEFORE_TRIM_END_SEC = 3;

export const VideoPreview: ParentComponent<Props> = (props) => {
	let videoRef!: HTMLVideoElement;
	const originatorId = createVideoPreviewOriginatorId();
	// Counts as soon as there is a URL, even one still pending, so the tile
	// keeps its hover-only loading instead of loading the video to fill the gap.
	const hasThumbnail = () => Boolean(props.thumbnailUrl);
	const thumbnail = usePendingImage(() => props.thumbnailUrl);
	const getClipBounds: VideoPreviewClipBounds = () => ({
		startSeconds: props.startSeconds ?? 0,
		endSeconds: props.endSeconds,
	});

	const [sourceActive, setSourceActive] = createSignal(false);
	// Sticky once the clip has played: it's then buffered, so we keep its source
	// loaded and skip the hover dwell for instant replay without more server hits.
	// A shared LRU caps how many previews stay warm at once (see registry).
	const [hasPlayed, setHasPlayed] = createSignal(false);
	const warmSlot = createWarmVideoSlot();
	onCleanup(() => warmSlot.remove());
	const { playing, playVideoOnHover, videoStyle, handleMouseLeave } =
		useVideoPreview(() => videoRef, hasThumbnail(), getClipBounds, {
			activateSource: () => setSourceActive(true),
			deactivateSource: () => setSourceActive(false),
			isWarm: () => hasPlayed(),
		});
	const [mediaDurationSeconds, setMediaDurationSeconds] = createSignal<
		number | null
	>(null);
	/** Absolute end of playable range in the source (seconds). */
	const clipEndAbsSeconds = createMemo(
		() => props.endSeconds ?? mediaDurationSeconds() ?? 0,
	);
	/** Length of the playable segment; UI timecode 0…this. */
	const clipDurationSeconds = createMemo(() => {
		const start = props.startSeconds ?? 0;
		return Math.max(0.001, clipEndAbsSeconds() - start);
	});
	/** Clip-relative position (seconds), 0 … clipDurationSeconds — matches Slider min/max. */
	const [time, setTime] = createSignal(0);
	const playTrigger = () => props.playTrigger ?? "hover";

	// Only lazy-load passive hover tiles that already show a thumbnail. Anything
	// driven by autoplay, on-screen controls, or an explicit `none` trigger keeps
	// eager loading so duration/dimensions/scrubbing keep working immediately.
	const lazyLoadSource = createMemo(
		() =>
			playTrigger() === "hover" &&
			hasThumbnail() &&
			props.autoPlay !== true &&
			!props.showControls,
	);
	const shouldLoadSource = createMemo(
		() => !lazyLoadSource() || sourceActive() || hasPlayed(),
	);

	// Drive the `<video>` source imperatively so we can fully reset the element
	// when the source is dropped — removing the attribute alone doesn't reliably
	// abort in-flight range requests (notably in Safari/WebKit), which can leave
	// the media server holding open connections until it's overwhelmed.
	let hlsHandle: HlsSourceHandle | undefined;
	onCleanup(() => hlsHandle?.destroy());

	createEffect(() => {
		const url = props.videoUrl;
		const hlsSource = props.hlsSource;
		const load = shouldLoadSource();
		untrack(() => {
			const el = videoRef;
			if (!el) return;

			hlsHandle?.destroy();
			hlsHandle = undefined;

			const setLegacySrc = () => {
				if (url) {
					if (el.getAttribute("src") !== url) {
						el.setAttribute("src", url);
						el.load();
					}
				} else if (el.hasAttribute("src")) {
					el.removeAttribute("src");
					el.load();
				}
			};

			if (!load) {
				if (el.hasAttribute("src")) {
					el.removeAttribute("src");
					el.load();
				}
				return;
			}

			if (hlsSource) {
				hlsHandle = attachHlsClip(
					el,
					hlsSource,
					setLegacySrc,
					props.startSeconds ?? 0,
				);
			} else {
				setLegacySrc();
			}
		});
	});

	// A new source hasn't been played yet, so drop the "warm" state — the new
	// clip must re-earn instant replay (and lazy tiles go back to dwell + unload).
	createEffect(() => {
		void props.videoUrl;
		setHasPlayed(false);
	});

	// Keep the shared warm-preview LRU in sync when this clip cools down (evicted,
	// source changed, or forced cold) so it no longer counts against the cap.
	createEffect(() => {
		if (!hasPlayed()) {
			warmSlot.remove();
		}
	});

	createEffect(() => {
		if (props.autoPlay !== true || props.forcePause || props.disabled) {
			return;
		}
		void props.videoUrl;
		void props.startSeconds;
		void props.endSeconds;

		let cancelled = false;
		let canPlayTarget: HTMLVideoElement | undefined;
		let canPlayHandler: (() => void) | undefined;
		const canAutoPlay = () =>
			!cancelled &&
			props.autoPlay === true &&
			!props.forcePause &&
			!props.disabled;

		onCleanup(() => {
			cancelled = true;
			if (canPlayTarget && canPlayHandler) {
				canPlayTarget.removeEventListener("canplay", canPlayHandler);
			}
		});

		untrack(() => {
			queueMicrotask(() => {
				const el = videoRef;
				if (!el || !canAutoPlay()) {
					return;
				}
				const start = props.startSeconds ?? 0;
				const end = props.endSeconds;
				if (el.currentTime < start || el.currentTime >= end) {
					el.currentTime = start;
				}
				el.play().catch(() => {
					if (!canAutoPlay()) {
						return;
					}
					canPlayTarget = el;
					canPlayHandler = () => {
						canPlayTarget = undefined;
						canPlayHandler = undefined;
						if (!canAutoPlay() || el !== videoRef) {
							return;
						}
						el.currentTime = props.startSeconds ?? 0;
						el.play().catch(() => {});
					};
					el.addEventListener("canplay", canPlayHandler, { once: true });
				});
			});
		});
	});

	/** When trim bounds change from the scrubber, jump playback to match the active handle. */
	let prevClipBoundsSyncUrl: string | undefined;
	let prevClipStartSeconds: number | undefined;
	let prevClipEndSeconds: number | undefined;

	createEffect(() => {
		const url = props.videoUrl;
		const start = props.startSeconds ?? 0;
		const end = clipEndAbsSeconds();

		if (prevClipBoundsSyncUrl !== url) {
			prevClipBoundsSyncUrl = url;
			prevClipStartSeconds = undefined;
			prevClipEndSeconds = undefined;
		}

		const startMoved =
			prevClipStartSeconds !== undefined && prevClipStartSeconds !== start;
		const endMoved =
			prevClipEndSeconds !== undefined && prevClipEndSeconds !== end;
		prevClipStartSeconds = start;
		prevClipEndSeconds = end;

		untrack(() => {
			const el = videoRef;
			if (!el) return;
			queueMicrotask(() => {
				if (startMoved) {
					el.currentTime = start;
					setTime(0);
					return;
				}
				if (endMoved) {
					const seekAbs = Math.max(
						start,
						end - PREVIEW_LEAD_BEFORE_TRIM_END_SEC,
					);
					el.currentTime = seekAbs;
					const rel = Math.max(0, seekAbs - start);
					const cap = Math.max(0.001, end - start);
					setTime(Math.min(rel, cap));
					return;
				}
				if (el.currentTime < start || el.currentTime >= end) {
					el.currentTime = start;
					setTime(0);
				}
			});
		});
	});

	createEffect(() => {
		const req = props.playbackSeekRequest?.();
		if (req == null) return;
		const { id, clipRelativeSeconds } = req;
		void id;
		void clipRelativeSeconds;
		untrack(() => {
			queueMicrotask(() => {
				const el = videoRef;
				if (!el) return;
				const start = props.startSeconds ?? 0;
				const end = props.endSeconds;
				const cap = Math.max(0.001, end - start);
				const r = Math.max(0, Math.min(clipRelativeSeconds, cap));
				el.currentTime = start + r;
				setTime(r);
			});
		});
	});

	const [isHovered, setIsHovered] = createSignal(false);
	const [mediaPlaying, setMediaPlaying] = createSignal(false);
	const [forceVideoVisible, setForceVideoVisible] = createSignal(false);

	const volume = useVolumeControl({
		originatorId,
		getVideoElement: () => videoRef,
		forceMuteAudio: () => props.forceMuteAudio,
		disabled: () => props.disabled,
	});

	const hasFooterContent = createMemo(
		() =>
			!!(
				props.footer ||
				props.title ||
				props.platform ||
				props.handle ||
				props.views !== undefined
			),
	);

	const footerHidden = createMemo(() => {
		if (props.showControls || props.footerHiddenOn === "never") {
			return false;
		}
		const interactive = playing() || isHovered();
		return (props.footerHiddenOn ?? "play") === "play"
			? interactive
			: !interactive;
	});

	createEffect(() => {
		const t = time();
		// Callback is often an inline arrow from parents; do not re-run this effect
		// when only the function identity changes.
		untrack(() => props.onPlaybackTime?.(t));
	});

	const mergedVideoStyle = (): JSX.CSSProperties => {
		const base = videoStyle();
		if (
			hasThumbnail() &&
			props.showControls &&
			(forceVideoVisible() || mediaPlaying())
		) {
			return { opacity: 1, display: "block" };
		}
		return base;
	};

	const togglePlay = () => {
		if (props.disabled || props.forcePause) {
			return;
		}
		const el = videoRef;
		if (!el) {
			return;
		}
		if (hasThumbnail() && props.showControls) {
			setForceVideoVisible(true);
		}
		if (el.paused) {
			const start = props.startSeconds ?? 0;
			const end = props.endSeconds;
			if (el.currentTime < start || el.currentTime >= end) {
				el.currentTime = start;
			}
			el.play().catch(() => {});
		} else {
			el.pause();
		}
	};

	createEffect(() => {
		if (!props.forcePause && !props.disabled) {
			return;
		}
		handleMouseLeave();
	});

	onMount(() => {
		const onPauseAll = () => {
			handleMouseLeave();
			setIsHovered(false);
			setForceVideoVisible(false);
		};
		document.addEventListener(VIDEO_PREVIEW_PAUSE_ALL_EVENT, onPauseAll);
		onCleanup(() => {
			document.removeEventListener(VIDEO_PREVIEW_PAUSE_ALL_EVENT, onPauseAll);
		});
	});

	return (
		// biome-ignore lint/a11y/noStaticElementInteractions: hover starts the preview playing, which is presentation; what activates it is the cover button inside, and a role here would make that button's own semantics unreachable.
		<div
			class={`video-grid-item gvtui-video-preview${props.portrait ? " gvtui-video-preview--portrait" : ""}${props.onActivate ? " gvtui-video-preview--activatable" : ""}${props.class ? ` ${props.class}` : ""}`}
			style={{
				"background-image": thumbnail.src()
					? `url(${thumbnail.src()})`
					: undefined,
				...props.style,
			}}
			onMouseEnter={() => {
				if (props.disabled || props.forcePause) {
					return;
				}
				if (playTrigger() === "hover") {
					playVideoOnHover(true);
				}
				setIsHovered(true);
			}}
			onMouseLeave={() => {
				if (props.showControls && mediaPlaying()) {
					setIsHovered(false);
					return;
				}
				if (playTrigger() === "hover") {
					handleMouseLeave();
				}
				setIsHovered(false);
			}}
		>
			<Video
				ref={(el) => {
					videoRef = el;

					const onPlay = () => {
						setMediaPlaying(true);
						setHasPlayed(true);
						// A clip actually played: the user is engaging with the grid, so
						// arm/extend browsing mode to make subsequent hovers near-instant.
						refreshBrowsingMode();
						// Mark warm / bump to most-recently-used; the release callback
						// cools this preview down if the shared cap evicts it.
						warmSlot.markWarm(() => setHasPlayed(false));
						props.onPlay?.();
					};
					const onPause = () => {
						setMediaPlaying(false);
						props.onPause?.();
					};

					const onTimeUpdate = () => {
						const start = props.startSeconds ?? 0;
						const end = clipEndAbsSeconds();
						if (el.currentTime < start) {
							el.currentTime = start;
						}
						if (el.currentTime >= end) {
							el.currentTime = start;
							if (props.loop === true && !props.forcePause && !props.disabled) {
								el.play().catch(() => {});
							} else {
								el.pause();
							}
							// Clip-relative end so consumers can detect completion; defer reset
							// so the playback-time effect can run before we snap back to 0.
							const cap = clipDurationSeconds();
							setTime(cap);
							queueMicrotask(() => setTime(0));
							return;
						}

						// Keep UI in sync with the element. Do not use `playing()` from hover
						// autoplay — when controls play, that is false and wrongly forced max time.
						const rel = Math.max(0, el.currentTime - start);
						const cap = clipDurationSeconds();
						setTime(Number(Math.min(rel, cap).toFixed(1)));
					};

					const onLoadedMetadata = () => {
						el.currentTime = props.startSeconds ?? 0;
						setTime(0);
						if (Number.isFinite(el.duration) && el.duration > 0) {
							setMediaDurationSeconds(el.duration);
							props.onVideoDuration?.(el.duration);
						}
						const vw = el.videoWidth;
						const vh = el.videoHeight;
						if (vw > 0 && vh > 0) {
							props.onVideoDimensions?.({ width: vw, height: vh });
						}
					};

					el.addEventListener("timeupdate", onTimeUpdate);
					el.addEventListener("loadedmetadata", onLoadedMetadata);
					el.addEventListener("play", onPlay);
					el.addEventListener("pause", onPause);
					const detachVolume = volume.attachToVideo(el);
					setMediaPlaying(!el.paused);

					onCleanup(() => {
						el.removeEventListener("timeupdate", onTimeUpdate);
						el.removeEventListener("loadedmetadata", onLoadedMetadata);
						el.removeEventListener("play", onPlay);
						el.removeEventListener("pause", onPause);
						detachVolume();
						el.pause();
					});
				}}
				autoplay={playing()}
				playsinline
				preload={shouldLoadSource() ? "metadata" : "none"}
				classList={{
					video: true,
					"gvtui-video-preview__video--controls": Boolean(props.showControls),
				}}
				style={mergedVideoStyle()}
			/>

			<Stack direction="column" spacing="small" class="video-overlay">
				{/* First, so that every control below it in this layer keeps the
				    clicks that land on it. */}
				<Show when={props.onActivate}>
					{(onActivate) => (
						<CoverButton
							label={props.activateLabel ?? "Open video"}
							onActivate={(event) => onActivate()(event)}
							disabled={props.disabled}
						/>
					)}
				</Show>
				<div class="top">
					<Show when={props.showDuration !== false}>
						<div class="video-duration-band">
							<Pill
								class={`video-duration${props.durationAction ? " video-duration--with-action" : ""}`}
								as={props.durationAction ? "button" : "span"}
								theme="dark"
								size="small"
								aria-label={props.durationAction?.label}
								onClick={
									props.durationAction
										? () => props.durationAction?.onActivate()
										: undefined
								}
							>
								{secondsToTime(
									playing() ? time() : clipDurationSeconds(),
									"MM:SS",
								)}
								<Show when={props.durationAction}>
									{(action) => (
										<MaterialSymbol
											class="video-duration__action-symbol"
											classList={{
												"video-duration__action-symbol--done": action().done,
											}}
											symbol={action().symbol}
										/>
									)}
								</Show>
							</Pill>
						</div>
					</Show>
				</div>
				<div
					class="footer"
					classList={{
						"footer--hidden": footerHidden(),
						"footer--no-gradient":
							props.footerHiddenOn === "always" || !hasFooterContent(),
					}}
				>
					<Show when={props.footer}>{props.footer}</Show>
					<Show when={!props.footer}>
						<Show when={props.title}>
							<div class="video-description">{props.title}</div>
						</Show>
						<Stack
							direction="row"
							spacing="medium"
							justify="space-between"
							align="center"
						>
							<Show when={props.platform || props.handle}>
								<Stack direction="row" spacing="small">
									<Show when={props.platform}>
										<div class="platform-icon">
											<BrandIcon
												width="1rem"
												height="1rem"
												platform={props.platform}
											/>
										</div>
									</Show>
									<Show when={props.handle}>
										<div class="handle">@{props.handle}</div>
									</Show>
								</Stack>
							</Show>

							<Show when={props.views !== undefined}>
								<Stack
									class="views"
									direction="row"
									spacing="small"
									align="center"
								>
									<Icon icon={visibilityIcon} width="1rem" height="1rem" />
									{formatNullableCount(props.views, { notation: "compact" })}{" "}
								</Stack>
							</Show>
						</Stack>
					</Show>
				</div>
				<Show when={props.showControls}>
					<div
						class="gvtui-video-preview__controls"
						onPointerDown={(e) => e.stopPropagation()}
					>
						<Button
							type="button"
							variant="text"
							theme="black"
							shape="circle"
							class="gvtui-video-preview__controls-play"
							symbol={mediaPlaying() ? "pause" : "play_arrow"}
							symbolRelativeSize="even-larger"
							aria-label={mediaPlaying() ? "Pause" : "Play"}
							onClick={(e) => {
								e.stopPropagation();
								togglePlay();
							}}
						/>
						<span class="gvtui-video-preview__controls-time" aria-hidden="true">
							{secondsToTime(time(), "MM:SS")}
						</span>
						<Slider
							min={0}
							max={clipDurationSeconds()}
							step={0.1}
							value={time}
							theme="white"
							aria-label="Seek"
							onChange={(relSeconds) => {
								setTime(relSeconds);
								if (props.disabled) {
									return;
								}
								const start = props.startSeconds ?? 0;
								const cap = clipDurationSeconds();
								videoRef.currentTime =
									start + Math.max(0, Math.min(relSeconds, cap));
							}}
						/>
						<span class="gvtui-video-preview__controls-time" aria-hidden="true">
							{secondsToTime(clipDurationSeconds(), "MM:SS")}
						</span>
						<VolumeControl controls={volume} />
					</div>
				</Show>
				<Show
					when={
						props.children &&
						(playing() || isHovered() || props.forceShowChildren)
					}
				>
					<div class="hover-actions">{props.children}</div>
				</Show>
			</Stack>
		</div>
	);
};
